import React, { useMemo } from "react";

import minBy from "lodash/minBy";
import maxBy from "lodash/maxBy";

import { Grid, Typography, InputAdornment } from "@material-ui/core";

import {
  ArrayInput,
  BooleanInput,
  DateInput,
  FormDataConsumer,
  Labeled,
  NumberField,
  NumberInput,
  RadioButtonGroupInput,
  required,
  SelectField,
  SelectInput,
  SimpleFormIterator,
  TextInput,
} from "react-admin";

import { theme } from "../../../../themes";
import { generateTimeInterval, parseTime } from "../../../../utils/dateUtils";
import {
  CalendarLayout,
  usHolidays,
  TimeZoneOptions,
} from "../../../../utils/constants";
import {
  HOLIDAY_RULE_KIND,
  coerceFormArrayField,
  holidayStringsToRules,
} from "../../../../utils/holidayRules";
import {
  validateEndTime,
  validatePositiveNumber,
  validateStartTime,
} from "../../eventTypes/EventTypeUtils";

const getTimeIntervals = (allValues, scopedFormData) => {
  if (!scopedFormData || !scopedFormData.selected) {
    return generateTimeInterval(); // Default full range if the day is unselected
  }

  const dayName = scopedFormData.name;
  const eventTypes = allValues.eventTypes?.items || [];

  if (!eventTypes.length) {
    return generateTimeInterval();
  }

  const timesForDay = eventTypes
    .flatMap((event) => event.schedule.workdays)
    .filter((wd) => wd.name === dayName && wd.selected);

  if (!timesForDay.length) {
    return generateTimeInterval();
  }

  const earliestStart = minBy(timesForDay, (wd) =>
    parseTime(wd.startDayTime)
  )?.startDayTime;
  const latestEnd = maxBy(timesForDay, (wd) =>
    parseTime(wd.endDayTime)
  )?.endDayTime;

  return generateTimeInterval(earliestStart, latestEnd, "not_available");
};

const validateRequired = [required()];

const hideIteratorItemLabel = () => null;

const holidayKindChoices = [
  {
    id: HOLIDAY_RULE_KIND.PRESET_RECURRING,
    name: "US HOLIDAY",
  },
  { id: HOLIDAY_RULE_KIND.CUSTOM_ONE_TIME, name: "Custom one-time" },
  { id: HOLIDAY_RULE_KIND.CUSTOM_RECURRING, name: "Custom recurring (yearly)" },
];

const presetHolidayChoices = Object.keys(usHolidays).map((key) => ({
  id: key,
  name: key,
}));

const holidayOutlineFieldProps = {
  variant: "outlined",
  fullWidth: true,
  validate: required(),
  helperText: false,
};

const HolidayRuleRowFields = ({ kind, getSource }) => {
  // A freshly-added row has no `kind` in scopedFormData until the SelectInput's
  // defaultValue commits, so mirror that default here or its fields stay hidden
  // until the user toggles the type.
  const activeKind = kind || HOLIDAY_RULE_KIND.PRESET_RECURRING;
  return (
  <Grid container spacing={2} alignItems="flex-start">
    <Grid item xs={12} md={3}>
      <SelectInput
        label="Type"
        source={getSource("kind")}
        {...holidayOutlineFieldProps}
        choices={holidayKindChoices}
        defaultValue={HOLIDAY_RULE_KIND.PRESET_RECURRING}
      />
    </Grid>
    {activeKind === HOLIDAY_RULE_KIND.PRESET_RECURRING && (
      <Grid item xs={12} md={8}>
        <SelectInput
          label="Holiday"
          source={getSource("presetKey")}
          {...holidayOutlineFieldProps}
          choices={presetHolidayChoices}
        />
      </Grid>
    )}
    {activeKind === HOLIDAY_RULE_KIND.CUSTOM_ONE_TIME && (
      <>
        <Grid item xs={12} md={4}>
          <TextInput
            label="Name"
            source={getSource("name")}
            {...holidayOutlineFieldProps}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <DateInput
            label="Date"
            source={getSource("date")}
            {...holidayOutlineFieldProps}
          />
        </Grid>
      </>
    )}
    {activeKind === HOLIDAY_RULE_KIND.CUSTOM_RECURRING && (
      <>
        <Grid item xs={12} md={3}>
          <TextInput
            label="Name"
            source={getSource("name")}
            {...holidayOutlineFieldProps}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <NumberInput
            label="Month (1-12)"
            source={getSource("month")}
            {...holidayOutlineFieldProps}
            min={1}
            max={12}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <NumberInput
            label="Day (1-31)"
            source={getSource("day")}
            {...holidayOutlineFieldProps}
            min={1}
            max={31}
          />
        </Grid>
      </>
    )}
  </Grid>
  );
};

const formatHolidayRuleLabel = (rule) => {
  if (!rule || typeof rule !== "object") {
    return "";
  }
  if (rule.kind === HOLIDAY_RULE_KIND.PRESET_RECURRING) {
    return rule.presetKey || rule.name || "Preset";
  }
  if (rule.kind === HOLIDAY_RULE_KIND.CUSTOM_ONE_TIME) {
    return `${rule.name || "One-time"} (${rule.date || "?"})`;
  }
  if (rule.kind === HOLIDAY_RULE_KIND.CUSTOM_RECURRING) {
    return `${rule.name || "Recurring"} (${rule.month ?? "?"}/${rule.day ?? "?"})`;
  }
  return rule.name || "Holiday";
};

export const SchedulePanel = ({ disabled, ...props }) => {
  if (disabled) return <ScheduleSummaryReadOnly {...props} />;
  return <ScheduleForm />;
};

const ScheduleForm = () => {
  return (
    <Grid container direction="row" spacing={2}>
      <Grid item xs={12} md={4}>
        <SelectInput
          source="timeZone"
          label="Time Zone"
          variant="outlined"
          fullWidth
          validate={required()}
          choices={TimeZoneOptions}
          helperText={false}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <NumberInput
          source="schedule.minNoticeHours"
          label="Min. Notice {Hours}"
          min={0}
          variant="outlined"
          fullWidth
          validate={validatePositiveNumber}
          helperText={false}
          InputProps={{
            endAdornment: <InputAdornment position="end">hours</InputAdornment>,
          }}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <NumberInput
          source="schedule.maxDaysIntoFuture"
          label="Max. Days into future"
          min={0}
          variant="outlined"
          fullWidth
          validate={validatePositiveNumber}
          helperText={false}
          InputProps={{
            endAdornment: <InputAdornment position="end">Days</InputAdornment>,
          }}
        />
      </Grid>

      <Grid container direction="row" spacing={2}>
        <Grid item xs={12} md={4}>
          <RadioButtonGroupInput
            source="schedule.layout"
            label="Week starts on"
            choices={[
              { id: CalendarLayout.WEEK_VIEW, name: "Sunday" },
              { id: CalendarLayout.TODAY_VIEW, name: "Current Day" },
            ]}
            optionText="name"
            optionValue="id"
            defaultValue={CalendarLayout.WEEK_VIEW}
            validate={validateRequired}
            helperText={false}
            style={{ marginTop: 0, marginLeft: theme.spacing(1) }}
          />
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Holidays and closures — add preset federal holidays, one-time
          closures, or yearly recurring custom dates.
        </Typography>
        <ArrayInput source="schedule.holidays" label="Holidays & closures">
          <SimpleFormIterator getItemLabel={hideIteratorItemLabel}>
            <FormDataConsumer>
              {({ scopedFormData, getSource }) => (
                <HolidayRuleRowFields
                  kind={scopedFormData?.kind}
                  getSource={getSource}
                />
              )}
            </FormDataConsumer>
          </SimpleFormIterator>
        </ArrayInput>
      </Grid>

      <Grid item xs={12} md={8}>
        <ArrayInput source={"schedule.workdays"} label="Work days" fullWidth>
          <SimpleFormIterator
            addButton={<></>}
            removeButton={<></>}
            reOrderButtons={<></>}
            getItemLabel={hideIteratorItemLabel}
          >
            <FormDataConsumer>
              {({
                formData,
                scopedFormData,
                getSource,
              }) => (
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <BooleanInput
                      label={scopedFormData?.name}
                      source={getSource("selected")}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <SelectInput
                      label="Start Time"
                      source={getSource("startDayTime")}
                      variant="outlined"
                      fullWidth
                      choices={getTimeIntervals(formData, scopedFormData)}
                      disableValue="not_available"
                      disabled={!scopedFormData?.selected}
                      helperText={false}
                      validate={[
                        (value, allValues) =>
                          validateStartTime(value, allValues, getSource),
                      ]}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <SelectInput
                      label="End Time"
                      source={getSource("endDayTime")}
                      variant="outlined"
                      fullWidth
                      choices={getTimeIntervals(formData, scopedFormData)}
                      disableValue="not_available"
                      disabled={!scopedFormData?.selected}
                      helperText={false}
                      validate={[
                        (value, allValues) =>
                          validateEndTime(value, allValues, getSource),
                      ]}
                    />
                  </Grid>
                </Grid>
              )}
            </FormDataConsumer>
          </SimpleFormIterator>
        </ArrayInput>
      </Grid>
    </Grid>
  );
};

const ScheduleSummaryReadOnly = (props) => {
  const { record } = props;
  const holidayRows = useMemo(
    () =>
      holidayStringsToRules(
        coerceFormArrayField(record?.schedule?.holidays),
        usHolidays,
      ),
    [record?.schedule?.holidays],
  );

  return (
    <Grid container direction="row" spacing={2}>
      <Grid item xs={12} md={6}>
        <Typography variant="caption" color="textSecondary">
          Holidays
        </Typography>
        {holidayRows.map((rule, idx) => {
          const label = formatHolidayRuleLabel(rule);
          return (
            <Typography key={idx}>{label}</Typography>
          );
        })}
      </Grid>
      <Grid item xs={12} md={6}>
        <Typography variant="caption" color="textSecondary">
          Workdays
        </Typography>
        {(record?.schedule?.workdays || [])
          .filter((itm) => itm.selected)
          .map((itm) => {
            return (
              <Typography key={itm.id || itm.name} variant="body1">
                {`${itm.name} ${itm.startDayTime}-${itm.endDayTime}`}
              </Typography>
            );
          })}
      </Grid>
      <Grid item xs={12} md={4}>
        <Labeled label="Min. Notice (Hours)">
          <NumberField source="schedule.minNoticeHours" />
        </Labeled>
      </Grid>
      <Grid item xs={12} md={4}>
        <Labeled label="Max. Days into future">
          <NumberField source="schedule.maxDaysIntoFuture" />
        </Labeled>
      </Grid>
      <Grid item xs={12} md={4}>
        <Labeled label="Time Zone">
          <SelectField
            source="timeZone"
            label="Time Zone"
            choices={TimeZoneOptions}
          />
        </Labeled>
      </Grid>
      <Grid item xs={12} md={4}>
        <Labeled label="Week starts on">
          <SelectField
            source="schedule.layout"
            choices={[
              { id: CalendarLayout.WEEK_VIEW, name: "Sunday" },
              { id: CalendarLayout.TODAY_VIEW, name: "Current Day" },
            ]}
          />
        </Labeled>
      </Grid>
    </Grid>
  );
};
